import { html, View } from 'rune-ts';
import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { isUndefined } from '@fxts/core';
import { SwiperOptions } from 'swiper/types';

export class NewSwiperChangeEvent<V extends View<object> = View> extends CustomEvent<{
  swiper: Swiper;
  active_view: V;
  views: V[];
  current_index: number;
  previous_index: number;
}> {}

export interface NewSwiperStyleType {
  swiper: string;
  wrapper: string;
}

export interface NewSwiperViewOptions<V extends View<object>> {
  swiperOptions: SwiperOptions;
  views: V[];
  styles?: NewSwiperStyleType;
}

/**
 * @todo SwiperView 컨트롤러 분리해서 로직 공유할 수 있도록 수정
 */
export class NewSwiperView<V extends View<object> = View<object>> extends View<NewSwiperViewOptions<V>> {
  swiper?: Swiper;
  active_view?: V;
  subviews?: V[];

  protected override template() {
    const { views, styles } = this.data;
    const swiperStyles = styles || this.getDefaultStyles();

    return html`
      <div class="${swiperStyles.swiper}">
        <div class="${this + 'Wrapper'} ${swiperStyles.wrapper}">${views}</div>
      </div>
    `;
  }

  protected override onRender() {
    const { swiperOptions, views } = this.data;
    this.subviews = views;

    const swiper = new Swiper(this.element(), {
      modules: [Pagination, Navigation, Autoplay, ...(swiperOptions.modules ?? [])],
      speed: 1000,
      wrapperClass: this + 'Wrapper',
      slideClass: views[0]?.constructor.toString() || 'slide',
      slideActiveClass: (views[0]?.constructor.toString() || 'slide') + 'Active',
      slideVisibleClass: (views[0]?.constructor.toString() || 'slide') + 'Visible',
      slideFullyVisibleClass: (views[0]?.constructor.toString() || 'slide') + 'FullyVisible',
      slideBlankClass: (views[0]?.constructor.toString() || 'slide') + 'Blank',
      slideNextClass: (views[0]?.constructor.toString() || 'slide') + 'Next',
      slidePrevClass: (views[0]?.constructor.toString() || 'slide') + 'Prev',
      resizeObserver: true,
      observer: true,
      autoplay: !isUndefined(swiperOptions.autoplay)
        ? swiperOptions.autoplay
        : {
            delay: 5000,
            pauseOnMouseEnter: true,
            waitForTransition: true,
          },
      ...swiperOptions,
    });

    let previous_index = swiper.realIndex;
    this.active_view = this.subviews[swiper.realIndex];

    swiper.on('realIndexChange', (swiper) => {
      if (previous_index === swiper.realIndex) return;

      this.active_view = this.subviews![swiper.realIndex];

      if (this.active_view) {
        this.dispatchEvent(NewSwiperChangeEvent, {
          bubbles: true,
          detail: {
            swiper,
            active_view: this.active_view,
            views: this.subviews!,
            current_index: swiper.realIndex,
            previous_index,
          },
        });
      }
      previous_index = swiper.realIndex;
    });

    this.swiper = swiper;
    this.subviews.map((view) => {
      if ('swiper' in view) {
        (view as any).swiper = swiper;
      }
    });
  }

  private getDefaultStyles(): NewSwiperStyleType {
    return {
      swiper: 'swiper',
      wrapper: 'swiper-wrapper',
    };
  }

  autoplayStop() {
    if (this.swiper) {
      this.swiper.autoplay.pause();
    }
  }

  autoplayPlay() {
    if (this.swiper) {
      this.swiper.autoplay.resume();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  prev() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }
}
